import { useSortable } from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import { ChevronRight, ChevronDown, Folder, MoreHorizontal } from 'lucide-react';
import clsx from 'clsx';
import { useAppStore } from '@/store/appStore';
import { InlineEditInput } from './InlineEditInput';
import { handleError } from '@/utils/errorHandler';

interface SortableFolderItemProps {
  folder: { name: string; taskCount: number; lists: Array<{ name: string; taskCount: number }> };
  folderId: string;
  spaceName: string;
  folderKey: string;
  isActive: boolean;
  /** True when a child item (list) is currently active */
  isAncestorActive?: boolean;
  isExpanded: boolean;
  onToggle: () => void;
  onClick: () => void;
  onContextMenu: (e: React.MouseEvent) => void;
  children: React.ReactNode;
  isEditing?: boolean;
  onCancel?: () => void;
}

export function SortableFolderItem({
  folder,
  folderId,
  spaceName,
  isActive,
  isAncestorActive,
  isExpanded,
  onToggle,
  onClick,
  onContextMenu,
  children,
}: SortableFolderItemProps) {
  const editingItems = useAppStore((state) => state.editingItems);
  const savePendingItemName = useAppStore((state) => state.savePendingItemName);
  const setEditingItem = useAppStore((state) => state.setEditingItem);
  const isEditing = editingItems.has(folderId);
  // Use both sortable (for dragging the folder) and droppable (for accepting drops)
  const {
    attributes,
    listeners,
    setNodeRef: setSortableRef,
    transform,
    transition,
    isDragging,
  } = useSortable({
    id: `folder-${folderId}`,
    data: {
      type: 'folder',
    },
  });

  const { setNodeRef: setDroppableRef, isOver } = useDroppable({
    id: `folder-${folderId}`,
    data: {
      type: 'folder',
      accepts: ['list', 'item'],
    },
  });

  // Combine refs
  const setRefs = (element: HTMLDivElement | null) => {
    setSortableRef(element);
    setDroppableRef(element);
  };

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div ref={setRefs} style={style} key={`${spaceName}/${folder.name}`}>
      <div
        {...attributes}
        {...(isEditing ? {} : listeners)} // Don't apply drag listeners when editing
        onClick={isDragging ? undefined : () => {
          onClick();
          if (!isExpanded) onToggle();
        }}
        onContextMenu={onContextMenu}
        className={clsx(
          'w-full flex items-center gap-1.5 px-2 py-1 rounded-md text-[10px] transition-colors group select-none',
          isDragging ? 'cursor-grabbing' : 'cursor-grab active:cursor-grabbing',
          isOver && !isDragging && 'ring-2 ring-accent-violet ring-inset bg-accent-violet/10', // Visual feedback when hovering
          isActive
            ? 'bg-accent-violet-dim text-accent-violet'
            : isAncestorActive
              ? 'text-text-secondary'
              : 'text-text-tertiary hover:bg-surface-hover hover:text-text-secondary'
        )}
      >
        <span
          onClick={(e) => {
            e.stopPropagation();
            onToggle();
          }}
          className="text-text-quaternary shrink-0 cursor-pointer hover:text-text-tertiary transition-colors"
        >
          {isExpanded ? (
            <ChevronDown className="w-2.5 h-2.5" />
          ) : (
            <ChevronRight className="w-2.5 h-2.5" />
          )}
        </span>
        <Folder className="w-3 h-3 text-accent-blue/70 shrink-0" />
        {isEditing ? (
          <InlineEditInput
            value={folder.name}
            placeholder="Nome cartella..."
            onSave={async (name) => {
              try {
                await savePendingItemName(folderId, name);
              } catch (error) {
                handleError(error, { level: 'error', message: 'Errore nel salvataggio del nome della cartella' });
                setEditingItem(folderId, false);
              }
            }}
            onCancel={() => {
              setEditingItem(folderId, false);
              // Remove temporary item if cancelled
              const currentSpaces = useAppStore.getState().spaces;
              const updatedSpaces = currentSpaces.map(space => {
                if (space.name !== spaceName) return space;
                return { ...space, folders: space.folders.filter(f => f.id !== folderId) };
              });
              useAppStore.getState().setSpaces(updatedSpaces);
            }}
            className="flex-1"
          />
        ) : (
          <span className="flex-1 text-left truncate">
            {folder.name}
          </span>
        )}
        {/* Count / Menu — menu replaces count on hover */}
        <span className="relative flex items-center justify-end min-w-[16px]">
          <span className="text-[10px] text-text-quaternary tabular-nums group-hover:opacity-0 transition-opacity">
            {folder.taskCount}
          </span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              onContextMenu(e);
            }}
            onMouseDown={(e) => e.stopPropagation()}
            className="absolute inset-0 flex items-center justify-center rounded opacity-0 group-hover:opacity-100 hover:bg-surface-tertiary transition-all"
          >
            <MoreHorizontal className="w-2.5 h-2.5 text-text-quaternary" />
          </button>
        </span>
      </div>

      {/* Children (lists, etc.) */}
      {children}
    </div>
  );
}
